import React from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction } from 'components/menu/items'
import { PanelAdd, PanelRule, PanelRules, PanelSegmentChoice, PanelSegmentSelect } from 'components/menu/panel'
import { ITableViewColumn } from 'types'
import { sortDirections } from 'components/spreadsheet/types'
import { BLOCK_SORT_BY, COLUMN_NAME } from 'components/spreadsheet/constants/const'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { directionOptions } from './directions'

const SortMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const sortSettings = spreadsheetData.userConfiguration.sortSettings
  const columns = spreadsheetData.viewDetails.columns
  const busy = spreadsheetData.refreshing

  const columnOption = (column: ITableViewColumn) => ({
    value: column.publicId,
    label: column.name,
    icon: <IconSelector kind={column.kind} />
  })
  // Columns a sort can be made from, other than the ones already sorted by.
  const freeColumns = columns.filter(
    (column) => !BLOCK_SORT_BY.includes(column.kind) && !sortSettings.some((sort) => sort.columnId === column.publicId)
  )

  const addSort = (columnId: string) => {
    const column = columns.find((c) => c.publicId === columnId)
    if (column) setSpreadsheetData({ type: 'ADD_SORT', columnId, columnName: column.name })
  }

  // Swap a sort's column in place, keeping its position and direction. (React batches the three.)
  const changeColumn = (index: number, columnId: string) => {
    const current = sortSettings[index]
    const column = columns.find((c) => c.publicId === columnId)
    if (!current || !column || current.columnId === columnId) return
    setSpreadsheetData({ type: 'DELETE_SORT', columnId: current.columnId })
    setSpreadsheetData({ type: 'ADD_SORT', columnId, columnName: column.name })
    setSpreadsheetData({ type: 'CHANGE_SORT_ORDER', existingIndex: sortSettings.length - 1, newIndex: index })
    if (current.direction !== sortDirections.asc) {
      setSpreadsheetData({ type: 'CHANGE_SORT_DIRECTION', columnId, newDirection: current.direction })
    }
  }

  const clearAll = () =>
    sortSettings.forEach((sort) => setSpreadsheetData({ type: 'DELETE_SORT', columnId: sort.columnId }))

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Sort"
      action={
        sortSettings.length > 0 && (
          <MenuHeaderAction onClick={clearAll} disabled={busy}>
            Clear all
          </MenuHeaderAction>
        )
      }
    >
      {sortSettings.length > 0 && (
        <PanelRules
          ids={sortSettings.map((sort) => sort.columnId)}
          onReorder={
            busy
              ? undefined
              : (existingIndex, newIndex) => setSpreadsheetData({ type: 'CHANGE_SORT_ORDER', existingIndex, newIndex })
          }
        >
          {sortSettings.map((sort, index) => {
            const column = columns.find((c) => c.publicId === sort.columnId)
            return (
              <PanelRule
                key={sort.columnId}
                id={sort.columnId}
                removeLabel={`Remove sort by ${sort.columnName}`}
                onRemove={() => setSpreadsheetData({ type: 'DELETE_SORT', columnId: sort.columnId })}
                disabled={busy}
                data-testid="sort-menu-sort-item"
              >
                <PanelSegmentSelect
                  ariaLabel="Column"
                  tone="field"
                  options={[...(column ? [columnOption(column)] : []), ...freeColumns.map(columnOption)]}
                  selected={[sort.columnId]}
                  onPick={(columnId) => changeColumn(index, columnId)}
                  menuWidth={240}
                  className="max-w-[200px]"
                  disabled={busy}
                />
                <PanelSegmentChoice
                  lead="sorted by"
                  ariaLabel={`Direction for ${sort.columnName}`}
                  options={directionOptions(column?.kind)}
                  value={sort.direction}
                  onChange={(newDirection) =>
                    setSpreadsheetData({ type: 'CHANGE_SORT_DIRECTION', columnId: sort.columnId, newDirection })
                  }
                  disabled={busy}
                />
              </PanelRule>
            )
          })}
        </PanelRules>
      )}

      <PanelAdd
        data-tour="add-sort"
        label="Add sort"
        options={freeColumns.map(columnOption)}
        onPick={addSort}
        disabled={busy}
        noOptionsMessage={`Every ${COLUMN_NAME} is already sorted by.`}
      />
    </FloatingMenu>
  )
}

export default SortMenu
